import { useRef, useState } from 'react';
import { CheckOutlined, PaperClipOutlined } from '@ant-design/icons';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';

import { Button, Dialog, Field, Select, Textarea } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { BYTE_MOI_MB, DINH_DANG_API, DINH_DANG_NGAY, TOI_DA_TEP_MB } from '../../nghiep-vu/nhiem-vu';
import type { DonViDanhBa, GiaoNhiemVuVao, NguoiThamGiaVao, NutNhiemVu } from '../../types-nhiem-vu';
import { ParticipantPicker } from './ParticipantPicker';

export type FormMode = { kind: 'create' } | { kind: 'child'; parent: NutNhiemVu } | { kind: 'edit'; task: NutNhiemVu };

const NOI_DUNG_TOI_DA = 2000;
const MO_TA_TOI_DA = 5000;

interface GiaTri {
  title: string;
  description: string;
  deadline: string;
  leadOrgId: string;
  participants: NguoiThamGiaVao[];
  file: File | null;
}

type Loi = Partial<Record<'title' | 'participants' | 'file', string>>;

function giaTriDau(mode: FormMode | null): GiaTri {
  if (mode?.kind === 'edit') {
    const x = mode.task;
    return {
      title: x.title,
      description: x.description ?? '',
      deadline: x.deadline ?? '',
      leadOrgId: x.lead_org_id ?? '',
      participants: x.participants.map(({ id: _id, ...p }): NguoiThamGiaVao => p),
      file: null,
    };
  }
  const cha = mode?.kind === 'child' ? mode.parent : null;
  return {
    title: '',
    description: '',
    deadline: cha?.deadline ?? '',
    leadOrgId: cha?.lead_org_id ?? '',
    participants: [],
    file: null,
  };
}

interface Props {
  mode: FormMode | null;
  orgs: DonViDanhBa[];
  saving: boolean;
  onClose: () => void;
  onCreate: (input: GiaoNhiemVuVao) => Promise<unknown>;
  onUpdate: (id: string, input: GiaoNhiemVuVao) => Promise<unknown>;
}

/** Hộp giao việc / việc con / sửa. Mở lại hộp (đổi `mode`) dựng lại form từ đầu nhờ `key` ở chỗ gọi. */
export function TaskFormDialog({ mode, orgs, saving, onClose, onCreate, onUpdate }: Props) {
  const { t } = useT();
  const fileRef = useRef<HTMLInputElement>(null);
  const [v, setV] = useState<GiaTri>(() => giaTriDau(mode));
  const [loi, setLoi] = useState<Loi>({});
  const doi = (thay: Partial<GiaTri>) => setV((cu) => ({ ...cu, ...thay }));

  const kiem = (): Loi => {
    const l: Loi = {};
    if (!v.title.trim()) l.title = t('vanBanNhiemVu.form.noiDungThieu');
    if (!v.participants.some((p) => p.role === 'CHU_TRI')) l.participants = t('vanBanNhiemVu.form.nguoiThieu');
    if (v.file && v.file.size > TOI_DA_TEP_MB * BYTE_MOI_MB) l.file = t('vanBanNhiemVu.form.tepQuaLon', { so: TOI_DA_TEP_MB });
    return l;
  };

  const gui = async () => {
    const l = kiem();
    setLoi(l);
    if (Object.keys(l).length > 0) return;
    const input: GiaoNhiemVuVao = {
      title: v.title.trim(),
      description: v.description.trim(),
      deadline: v.deadline,
      leadOrgId: v.leadOrgId,
      leadOrgName: orgs.find((o) => o.id === v.leadOrgId)?.name ?? '',
      file: v.file,
      participants: v.participants,
      parentId: mode?.kind === 'child' ? mode.parent.id : undefined,
    };
    try {
      if (mode?.kind === 'edit') await onUpdate(mode.task.id, input);
      else await onCreate(input);
      onClose();
    } catch {
      // Hook đã báo lỗi qua toast; giữ hộp mở để người dùng sửa rồi gửi lại.
    }
  };

  const tieuDe =
    mode?.kind === 'edit'
      ? t('vanBanNhiemVu.form.tieuDeSua')
      : mode?.kind === 'child'
        ? t('vanBanNhiemVu.form.tieuDeCon')
        : t('vanBanNhiemVu.form.tieuDeTao');
  const tepCu = mode?.kind === 'edit' ? mode.task.attachment_filename : null;

  return (
    <Dialog
      open={mode !== null}
      onClose={() => !saving && onClose()}
      title={tieuDe}
      width={640}
      destroyOnHidden
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t('chung.huy')}
          </Button>
          <Button variant="primary" icon={<CheckOutlined />} loading={saving} onClick={() => void gui()}>
            {saving ? t('chung.dangLuu') : mode?.kind === 'edit' ? t('vanBanNhiemVu.form.nutSua') : t('vanBanNhiemVu.form.nutTao')}
          </Button>
        </div>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void gui();
        }}
        className="space-y-4 pt-2"
      >
        {mode?.kind === 'child' && (
          <p className="rounded-lg bg-subtle px-3 py-2 text-xs text-content-secondary">
            {t('vanBanNhiemVu.form.thuoc', { ten: mode.parent.title })}
            <br />
            {t('vanBanNhiemVu.form.chaTuCapNhat')}
          </p>
        )}

        <Field label={t('vanBanNhiemVu.form.noiDung')} required error={loi.title}>
          {(f) => (
            <Textarea
              {...f}
              autoSize={{ minRows: 2, maxRows: 4 }}
              maxLength={NOI_DUNG_TOI_DA}
              placeholder={t('vanBanNhiemVu.form.noiDungGoiY')}
              value={v.title}
              onChange={(e) => doi({ title: e.target.value })}
            />
          )}
        </Field>

        <Field label={t('vanBanNhiemVu.form.moTa')}>
          {(f) => (
            <Textarea
              {...f}
              autoSize={{ minRows: 2, maxRows: 6 }}
              maxLength={MO_TA_TOI_DA}
              placeholder={t('vanBanNhiemVu.form.moTaGoiY')}
              value={v.description}
              onChange={(e) => doi({ description: e.target.value })}
            />
          )}
        </Field>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label={t('vanBanNhiemVu.form.donVi')}>
            {(f) => (
              <Select<string>
                id={f.id}
                className="w-full"
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder={t('vanBanNhiemVu.form.donViGoiY')}
                value={v.leadOrgId || undefined}
                onChange={(x) => doi({ leadOrgId: x ?? '' })}
                options={orgs.map((o) => ({ value: o.id, label: o.name }))}
              />
            )}
          </Field>
          <Field label={t('vanBanNhiemVu.form.han')}>
            {(f) => (
              <DatePicker
                id={f.id}
                className="w-full"
                format={DINH_DANG_NGAY}
                placeholder={t('vanBanNhiemVu.form.hanGoiY')}
                value={v.deadline ? dayjs(v.deadline, DINH_DANG_API) : null}
                onChange={(d) => doi({ deadline: d ? d.format(DINH_DANG_API) : '' })}
              />
            )}
          </Field>
        </div>

        <Field label={t('vanBanNhiemVu.form.nguoi')} required hint={t('vanBanNhiemVu.form.nguoiMo')} error={loi.participants}>
          {(f) => (
            <ParticipantPicker
              id={f.id}
              value={v.participants}
              onChange={(participants) => doi({ participants })}
              orgs={orgs}
              invalid={f['aria-invalid']}
            />
          )}
        </Field>

        <Field label={t('vanBanNhiemVu.form.tep')} hint={t('vanBanNhiemVu.form.tepMo', { so: TOI_DA_TEP_MB })} error={loi.file}>
          {(f) => (
            <div className="flex flex-wrap items-center gap-2">
              <input
                ref={fileRef}
                id={f.id}
                type="file"
                className="hidden"
                onChange={(e) => doi({ file: e.target.files?.[0] ?? null })}
              />
              <Button variant="outline" icon={<PaperClipOutlined />} onClick={() => fileRef.current?.click()}>
                {t('vanBanNhiemVu.form.chonTep')}
              </Button>
              <span className="min-w-0 truncate text-xs text-content-secondary">
                {v.file?.name ?? (tepCu ? t('vanBanNhiemVu.form.tepHienTai', { ten: tepCu }) : '')}
              </span>
            </div>
          )}
        </Field>
      </form>
    </Dialog>
  );
}
